import React, {useEffect, useState} from 'react';
import _ from 'lodash';
import {showDollarAmountWithDollar} from 'common/utils';
import DynamicTable from 'componentLibrary/layout/dynamicTable';
import {FemaGoTable} from 'app-ui-componentlibrary';
import {connectDynamic} from 'go/components/dynamic/dynamicComponent';
import {withRouter} from 'react-router-dom';
import {Grid, Header} from 'semantic-ui-react';

// reductions = [{sourceOfFunding: xxx, id: xxx}]
export const ReductionTable = (props) => {
  const {
    id,
    mode,
    reductions,
    header,
    entityType,
    entityDataPath,
    compareDataPath,
    reductionTotalPath,
    validationId,
    validationErrors,
    context,
    validationDirtyMap,
    data,
  } = props;

  // store the config if there is nothing already...
  useEffect(() => {
    if (!data && (mode === 'edit' || mode === 'review')) {
      props.saveEntityData(entityDataPath, entityType, reductions);
    }
  }, []);

  // get the current state, or if none exist, use the config as a starting point.
  const [reductionsState, setReductions] = useState(data || reductions);
  const [dirty, setDirty] = useState(false);

  const handleChange = (idx, name) => (e1, e2) => {
    setDirty(true);
    e2 = e2 ?? '';
    setReductions(
      _.filter(
        [
          ..._.slice(reductionsState, 0, idx),
          {
            ...reductionsState[idx],
            [name]: _.truncate(e2.value ?? e2, {length: 200, omission: ''}),
          },
          ..._.slice(reductionsState, idx + 1),
        ],
        (t) => t
      )
    );
  };
  const calculateTotalReductions = (reductionTotalPath, entityType, reductionsState) => {
    const reductionAmounts = _.map(reductionsState, (reduction) =>
      !reduction.amount || isNaN(reduction.amount) ? 0 : parseFloat(reduction.amount)
    );
    const reductionTotal = _.sum(reductionAmounts);

    props.saveEntityData(reductionTotalPath, entityType, reductionTotal);
  };

  const handleBlur = (idx, name) => (e1, e2) => {
    setDirty(true);
    calculateTotalReductions(reductionTotalPath, entityType, reductionsState);
    props.saveEntityData(entityDataPath, entityType, reductionsState);
  };

  const checkDiffs = () => {
    return _.map(reductionsState, (reduction, index) => {
      return (
        _.find(subAppVals, {id: reduction.id}).amount == _.get(reduction, 'amount', 0)
      );
    });
  };

  const fieldValidation = (id, field) => {
    const reductionIndex = _.findIndex(
      reductionsState,
      (reduction) => reduction.id === id
    );
    return _.get(validationErrors, `0.${id}.0.error.${reductionIndex}.${field}`);
  };

  const hasFieldError = (id, field) => {
    // no error if no validation present
    const reductionIndex = _.findIndex(
      reductionsState,
      (reduction) => reduction.id === id
    );
    return _.get(validationErrors, `0.${id}.0.error.${reductionIndex}.isValid`) === false;
  };

  const fieldErrorMessage = (id, field) => {
    let errorMessage = '';
    _.forEach(fieldValidation(id, field), (v) => {
      if (!_.get(v, 'isValid')) {
        errorMessage = _.get(v, 'message');
      }
    });
    return errorMessage;
  };

  const displayFieldError = (id, field) => {
    return hasFieldError(id, field) && (dirty || validationDirtyMap?.dirty);
  };

  const firstError =
    _.find(_.get(validationErrors, '0.reductions'), (err) => !err.isValid) || false;
  const errorMessage = firstError ? firstError.message : false;
  const subAppVals = _.get(context.subApplicationGrant, compareDataPath, [
    {id: 'insurance', amount: 0},
  ]);
  const displayError = firstError && (dirty || validationDirtyMap?.dirty);
  const errorMessages = errorMessage;

  return (
    <Grid key={`${id}-grid`} doubling stackable reversed="mobile" padded="vertically">
      <Grid.Column className="visible-overflow">
        <b>{header}</b>
        {displayError && <div className={'errorMessages'}>{errorMessages}</div>}
        <FemaGoTable summary={header} type="static" columns="3">
          <FemaGoTable.Header>
            <FemaGoTable.Row>
              <FemaGoTable.HeaderCell title="Source of funding">
                Source of funding
              </FemaGoTable.HeaderCell>
              <FemaGoTable.HeaderCell title="Amount">Amount</FemaGoTable.HeaderCell>
              <FemaGoTable.HeaderCell title="Description">
                Description
              </FemaGoTable.HeaderCell>
            </FemaGoTable.Row>
          </FemaGoTable.Header>
          <FemaGoTable.Body>
            {_.map(reductionsState, (reduction, index) =>
              mode === 'edit' || mode === 'review' ? (
                <FemaGoTable.Row key={`${id}-${reduction.id}-row`}>
                  <FemaGoTable.TextCell>
                    {_.get(reduction, 'sourceOfFunding')}
                  </FemaGoTable.TextCell>
                  <FemaGoTable.DollarCell
                    id={`${id}-${reduction.id}-amount`}
                    name={`${id}-${reduction.id}-amount`}
                    title="Amount"
                    value={_.get(reduction, 'amount', 0) || 0}
                    handleChange={handleChange(index, 'amount')}
                    handleBlur={handleBlur(index, 'amount')}
                    displayError={displayFieldError(reduction.id, 'amount')}
                    errorMessages={fieldErrorMessage(reduction.id, 'amount')}
                  ></FemaGoTable.DollarCell>
                  {mode === 'review' ? (
                    <FemaGoTable.TextCell>
                      {_.get(reduction, 'description')}
                    </FemaGoTable.TextCell>
                  ) : (
                    <FemaGoTable.TextAreaCell
                      id={`${id}-${reduction.id}-description`}
                      name={`${id}-${reduction.id}-description`}
                      value={_.get(reduction, 'description')}
                      handleChange={handleChange(index, 'description')}
                      handleBlur={handleBlur(index, 'description')}
                      displayError={displayFieldError(reduction.id, 'description')}
                      errorMessages={fieldErrorMessage(reduction.id, 'description')}
                    ></FemaGoTable.TextAreaCell>
                  )}
                </FemaGoTable.Row>
              ) : (
                <FemaGoTable.Row key={`${id}-${reduction.id}-row`}>
                  <FemaGoTable.TextCell>
                    {_.get(reduction, 'sourceOfFunding')}
                  </FemaGoTable.TextCell>
                  <FemaGoTable.TextCell>
                    {showDollarAmountWithDollar(_.get(reduction, 'amount'))}
                  </FemaGoTable.TextCell>
                  <FemaGoTable.TextCell>
                    {_.get(reduction, 'description')}
                  </FemaGoTable.TextCell>
                </FemaGoTable.Row>
              )
            )}
          </FemaGoTable.Body>
        </FemaGoTable>
        {mode === 'review' && !checkDiffs().some((v) => v) && (
          <div className="diffingBackground" style={{padding: '8px'}}>
            <Header as="h5">{'Reviewer Edits'}</Header>
            {_.map(reductionsState, (reduction, index) => {
              return (
                <p key={index}>
                  <span>
                    {'Amount from '}
                    <strong>
                      {showDollarAmountWithDollar(
                        _.find(subAppVals, {id: reduction.id}).amount
                      )}
                    </strong>
                    {' to '}
                    <strong>
                      {showDollarAmountWithDollar(_.get(reduction, 'amount', 0) || 0)}
                    </strong>
                  </span>
                </p>
              );
            })}
          </div>
        )}
      </Grid.Column>
    </Grid>
  );
};

export default connectDynamic(withRouter(ReductionTable));
